import { getLocalTime } from '@gripp/shared-logic';

// Shared helpers for serializing routine submission answers into CSV cells.
// Extracted from downloadRoutine.tsx so the data-plate export hook can reuse
// them without depending on the routine-export component (avoids a cycle).

export const RESPONSE_VALUE_KEYS = [
  'value',
  'confirmedValue',
  'count',
  'status',
  'option',
  'options',
  'mediaIds',
  'timeTypeValue',
  'barcodes',
] as const;

export type ResponseValueKey = (typeof RESPONSE_VALUE_KEYS)[number];

// Response value keys whose answers are lists — these are the "multiples"
// (barcode scans, images, multi-selects) that split mode spreads across one
// column per value instead of comma-joining into a single cell. Single-value
// keys (option, value, status, …) are never split.
export const MULTI_VALUE_KEYS = new Set<ResponseValueKey>([
  'barcodes',
  'mediaIds',
  'options',
]);

export type BarcodeExportEntry = {
  value?: unknown;
  quantity?: unknown;
  memo?: unknown;
};

// react-csv 2.x wraps values in double-quotes but does NOT escape inner double-quotes,
// producing malformed CSV when values contain characters like inch marks (e.g. .010").
// Sanitize all string output to avoid breaking column alignment in the exported file.
export function sanitizeCsvString(value: string): string {
  return value.replace(/"/g, "'");
}

function formatSingleBarcode(entry: BarcodeExportEntry): string {
  if (!entry || typeof entry !== 'object') {
    return '';
  }

  const barcodeValue = typeof entry.value === 'string' ? entry.value : '';
  if (!barcodeValue) {
    return '';
  }

  return entry.quantity !== undefined
    ? `${barcodeValue} (${entry.quantity})`
    : barcodeValue;
}

function formatBarcodeEntries(value: BarcodeExportEntry[]): string {
  return sanitizeCsvString(
    value.map(formatSingleBarcode).filter(Boolean).join(', ')
  );
}

/**
 * Splits a single response into its individual scalar values for "split to rows"
 * mode. Multi-value answers (barcodes, images/mediaIds, multi-select options)
 * yield one entry each; single-value answers yield exactly one (reusing
 * formatResponseValue so timeType/open-ended formatting stays consistent).
 */
export function extractIndividualValues(
  response: Record<string, unknown>,
  valueKey?: ResponseValueKey
): string[] {
  if (!valueKey) {
    return [];
  }

  const value = response[valueKey];

  if (valueKey === 'barcodes' && Array.isArray(value)) {
    return value
      .map((entry) => formatSingleBarcode(entry as BarcodeExportEntry))
      .filter(Boolean)
      .map(sanitizeCsvString);
  }

  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === 'string' ? sanitizeCsvString(item) : String(item)
      )
      .filter((item) => item !== '');
  }

  const formatted = formatResponseValue(response, valueKey);
  if (formatted === '' || formatted === undefined || formatted === null) {
    return [];
  }

  return [formatted as string];
}

export type BarcodeField = 'qty' | 'memo';

// Column-key convention for the extra barcode sub-columns in split-to-rows
// mode. The barcode value stays on the question's primary key (questionId);
// quantity and memo get their own suffixed keys/columns.
export const barcodeColumnKey = (questionId: string, field: BarcodeField) =>
  `${questionId}__${field}`;

// Column-key convention for the End sub-column of a "Start & end times" question.
// The start time stays on the question's primary key (questionId); the end time
// gets its own suffixed key/column.
export const endTimeColumnKey = (questionId: string) => `${questionId}__end`;

export type BarcodeColumnPresence = { quantity: boolean; memo: boolean };

/**
 * Scans every submission to decide which barcode sub-columns a barcode question
 * needs in split-to-rows mode: the Barcode (value) column always, a Qty column
 * only if some entry carries a quantity, and a Memo column only if some entry
 * has a non-empty memo. Returns a map keyed by questionId for every barcode
 * question found in the data.
 */
export function detectBarcodeColumns(
  submissions: Array<{ responses?: SubmissionResponse[] }>
): Map<string, BarcodeColumnPresence> {
  const byQid = new Map<string, BarcodeColumnPresence>();

  submissions.forEach((submission) => {
    submission.responses?.forEach((response) => {
      if (!Array.isArray(response.barcodes)) {
        return;
      }
      const presence = byQid.get(response.questionId) ?? {
        quantity: false,
        memo: false,
      };
      (response.barcodes as BarcodeExportEntry[]).forEach((entry) => {
        if (!entry || typeof entry !== 'object') {
          return;
        }
        if (entry.quantity !== undefined && entry.quantity !== null) {
          presence.quantity = true;
        }
        if (typeof entry.memo === 'string' && entry.memo !== '') {
          presence.memo = true;
        }
      });
      byQid.set(response.questionId, presence);
    });
  });

  return byQid;
}

/**
 * Returns the per-row cells for one response in split-to-rows mode. Each element
 * is the set of column-key -> value to write on that row:
 *
 *  - Barcode answers spread one entry per row and break the entry into its own
 *    columns: value on questionId, quantity on questionId__qty, memo on
 *    questionId__memo (quantity/memo only when the entry actually has them).
 *  - Other multi-value answers (images, multi-selects) spread one scalar value
 *    per row into the single questionId column.
 */
export function extractRowCells(
  response: SubmissionResponse,
  questionId: string,
  valueKey: ResponseValueKey
): ExportRow[] {
  if (valueKey === 'barcodes' && Array.isArray(response.barcodes)) {
    return (response.barcodes as BarcodeExportEntry[])
      .filter((entry) => entry && typeof entry === 'object')
      .map((entry) => {
        const value =
          typeof entry.value === 'string' ? sanitizeCsvString(entry.value) : '';
        const cells: ExportRow = { [questionId]: value };
        if (entry.quantity !== undefined && entry.quantity !== null) {
          cells[barcodeColumnKey(questionId, 'qty')] = sanitizeCsvString(
            String(entry.quantity)
          );
        }
        if (typeof entry.memo === 'string' && entry.memo !== '') {
          cells[barcodeColumnKey(questionId, 'memo')] = sanitizeCsvString(
            entry.memo
          );
        }
        return cells;
      })
      .filter((cells) => cells[questionId] !== '');
  }

  return extractIndividualValues(response, valueKey).map((value) => ({
    [questionId]: value,
  }));
}

/**
 * Non-split-mode barcode formatting. Barcode answers split ACROSS into Barcode /
 * Qty / Memo columns in BOTH export modes (like counters and start/end times) —
 * only the row spreading (one scan per row) is gated on split-to-rows. This is
 * the single-row path: it reuses extractRowCells to break each scan into its
 * value/qty/memo cells (so extraction stays identical to the split path), then
 * comma-joins each field into its own column so the three columns line up
 * positionally on the submission's one row. A field is left blank when no scan
 * carries it, so the Qty/Memo columns don't fill with lone separators.
 */
export function formatBarcodeColumns(
  response: SubmissionResponse,
  questionId: string
): Record<string, string> {
  const perScan = extractRowCells(response, questionId, 'barcodes');
  const qtyKey = barcodeColumnKey(questionId, 'qty');
  const memoKey = barcodeColumnKey(questionId, 'memo');

  const join = (key: string) => {
    const parts = perScan.map((cells) => {
      const value = cells[key];
      return typeof value === 'string' ? value : '';
    });
    return parts.some((part) => part !== '') ? parts.join(', ') : '';
  };

  return {
    [questionId]: join(questionId),
    [qtyKey]: join(qtyKey),
    [memoKey]: join(memoKey),
  };
}

export function getResponseKey(
  response: Record<string, unknown>
): ResponseValueKey | undefined {
  const keys = Object.keys(response);
  return keys.find((key): key is ResponseValueKey =>
    RESPONSE_VALUE_KEYS.includes(key as ResponseValueKey)
  );
}

export function formatResponseValue(
  response: Record<string, unknown>,
  valueKey?: ResponseValueKey
) {
  if (!valueKey) {
    return '';
  }

  const value = response[valueKey];

  if (valueKey === 'barcodes' && Array.isArray(value)) {
    return formatBarcodeEntries(value as BarcodeExportEntry[]);
  }

  if (Array.isArray(value)) {
    return sanitizeCsvString(value.join(', '));
  }

  if (value && valueKey === 'timeTypeValue') {
    // Each TimeType variation stores a distinguishable value shape, so we format
    // by shape to match the type the routine builder selected:
    //   - { startTime, endTime } (Start & end times) -> time only, e.g. "2:30 PM - 4:00 PM"
    //   - { date, time }         (Time & date)        -> "06/29/2026 2:30 PM"
    //   - "MM/DD/YYYY" string    (Date only)          -> raw date
    //   - ISO string             (Time only)          -> time only, e.g. "2:30 PM"
    if (typeof value === 'object') {
      if ('startTime' in value && 'endTime' in value) {
        return `${getLocalTime(
          (value as { startTime: string }).startTime
        )} - ${getLocalTime((value as { endTime: string }).endTime)}`;
      }
      if ('date' in value || 'time' in value) {
        const { date, time } = value as { date?: string; time?: string };
        return [date ?? '', time ? getLocalTime(time) : '']
          .filter(Boolean)
          .join(' ');
      }
    } else if (typeof value === 'string') {
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        return getLocalTime(value);
      }
      return value;
    }
  } else if (
    valueKey === 'status' &&
    value === 'openEndedResponse' &&
    response.openEndedResponse
  ) {
    return sanitizeCsvString(response.openEndedResponse as string);
  }

  if (typeof value === 'string') {
    return sanitizeCsvString(value);
  }

  return value;
}

export type StartEndTimeValue = { startTime: string; endTime: string };

// A "Start & end times" answer is the only timeTypeValue stored as an object
// carrying both startTime and endTime (Time-only / Date-only store strings;
// Time-and-date stores { date, time }). Mirrors formatResponseValue's shape check.
export function isStartEndTimeValue(
  value: unknown
): value is StartEndTimeValue {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const v = value as Record<string, unknown>;
  return typeof v.startTime === 'string' && typeof v.endTime === 'string';
}

/**
 * Scans every submission for "Start & end times" answers and returns the set of
 * question IDs that should export as two columns (Start / End) instead of a
 * single joined cell. Mirrors detectBarcodeColumns: a question only splits when
 * the data actually carries a start/end value for it.
 */
export function detectStartEndTimeColumns(
  submissions: Array<{ responses?: SubmissionResponse[] }>
): Set<string> {
  const ids = new Set<string>();

  submissions.forEach((submission) => {
    submission.responses?.forEach((response) => {
      if (isStartEndTimeValue(response.timeTypeValue)) {
        ids.add(response.questionId);
      }
    });
  });

  return ids;
}

/**
 * Formats a "Start & end times" answer into its two column cells: the start time
 * on the question's primary key and the end time on endTimeColumnKey. A missing
 * bound renders blank. Uses the same per-bound getLocalTime formatting as the
 * joined-cell path in formatResponseValue, just spread across two columns.
 */
export function formatStartEndTime(
  response: SubmissionResponse
): Record<string, string> {
  const value = response.timeTypeValue as StartEndTimeValue;
  const questionId = response.questionId;

  return {
    [questionId]: value?.startTime ? getLocalTime(value.startTime) : '',
    [endTimeColumnKey(questionId)]: value?.endTime
      ? getLocalTime(value.endTime)
      : '',
  };
}

// A single routine submission answer. Always carries a questionId; the rest of
// the shape depends on the question type (value/option/options/barcodes/…).
export type SubmissionResponse = { questionId: string } & Record<
  string,
  unknown
>;

// One row in the exported CSV, keyed by column key (base columns + questionId).
// Keys that are absent render as an empty cell (react-csv), which is how split
// mode blanks out every cell that isn't relevant to an extra row.
export type ExportRow = Record<string, unknown>;

/**
 * Builds the CSV row(s) for a single routine submission.
 *
 * Normally returns one row. In split-to-rows mode, each multi-value answer
 * (barcode scans, images, multi-select options) with more than one value
 * spreads DOWN: its first value stays on the submission's primary row and every
 * additional value becomes its own row below. Those extra rows contain ONLY the
 * relevant question's cell — the base columns, single-value questions, and any
 * shorter multi-value answers are intentionally omitted so they render blank.
 */
export function buildSubmissionRows(
  responses: SubmissionResponse[] | undefined,
  primaryRowSeed: ExportRow,
  options: {
    splitToRows: boolean;
    multiColumnCounterIds: Set<string>;
    formatCounterData: (response: SubmissionResponse) => Record<string, string>;
  }
): ExportRow[] {
  const { splitToRows, multiColumnCounterIds, formatCounterData } = options;

  const primaryRow: ExportRow = { ...primaryRowSeed };

  // Multi-value answers that spread down: questionId -> per-row cells. Each
  // element is the set of column-key -> value for that row (a barcode row
  // fills value/qty/memo columns; other answers fill the single questionId
  // column). Index 0 is merged onto the primary row; indexes 1..n become the
  // extra rows below.
  const spreadRowsByQid = new Map<string, ExportRow[]>();

  responses?.forEach((response) => {
    const qid = response.questionId;

    if (multiColumnCounterIds.has(qid)) {
      Object.assign(primaryRow, formatCounterData(response));
      return;
    }

    const valueKey = getResponseKey(response);

    if (splitToRows && valueKey && MULTI_VALUE_KEYS.has(valueKey)) {
      const rowCells = extractRowCells(response, qid, valueKey);
      if (rowCells.length > 0) {
        // The first value's cells stay on the submission's primary row.
        Object.assign(primaryRow, rowCells[0]);
      }
      if (rowCells.length > 1) {
        spreadRowsByQid.set(qid, rowCells);
      }
      return;
    }

    // Barcode answers split ACROSS into Barcode / Qty / Memo columns in normal
    // mode too (matching counters and start/end times). In split-to-rows mode
    // the branch above already spread them DOWN into one row per scan.
    if (valueKey === 'barcodes' && Array.isArray(response.barcodes)) {
      Object.assign(primaryRow, formatBarcodeColumns(response, qid));
      return;
    }

    // "Start & end times" answers spread ACROSS into two columns (Start on qid,
    // End on endTimeColumnKey) rather than joining into one cell. Applies in both
    // normal and split-to-rows mode — it's a column split, not a row split.
    if (valueKey === 'timeTypeValue' && isStartEndTimeValue(response.timeTypeValue)) {
      Object.assign(primaryRow, formatStartEndTime(response));
      return;
    }

    primaryRow[qid] = formatResponseValue(response, valueKey);
  });

  if (spreadRowsByQid.size === 0) {
    return [primaryRow];
  }

  // The submission needs as many rows as its longest multi-value answer.
  const rowCount = Math.max(
    ...Array.from(spreadRowsByQid.values(), (rowCells) => rowCells.length)
  );

  const rows: ExportRow[] = [primaryRow];
  for (let i = 1; i < rowCount; i++) {
    const extraRow: ExportRow = {};
    for (const [, rowCells] of spreadRowsByQid) {
      if (i < rowCells.length) {
        Object.assign(extraRow, rowCells[i]);
      }
    }
    rows.push(extraRow);
  }

  return rows;
}
